import React from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import { FaHistory, FaLeaf, FaHandsHelping, FaQuoteLeft, FaChargingStation, FaSolarPanel, FaBolt } from 'react-icons/fa';
import { IconWrapper } from '../components/icons/IconWrapper';
import { DefaultTheme } from 'styled-components';

const PageContainer = styled.div`
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme.colors.darkest};
  color: ${({ theme }: { theme: DefaultTheme }) => theme.colors.light};
`;

const Section = styled.section`
  padding: 5rem 0;
  max-width: 1200px;
  margin: 0 auto;
  padding: 5rem 1rem;
`;

const HeroSection = styled.section`
  position: relative;
  height: 50vh;
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  background: linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), 
              url('https://images.unsplash.com/photo-1544197150-b99a580bb7a8?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80');
  background-size: cover;
  background-position: center;
  
  @media (max-width: ${({ theme }: { theme: DefaultTheme }) => theme.breakpoints.md}) {
    height: 40vh;
    min-height: 300px;
    background-attachment: scroll;
  }
`;

const HeroTitle = styled.h1`
  font-size: 4rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: ${({ theme }: { theme: DefaultTheme }) => theme.colors.light};
  
  span {
    color: ${({ theme }: { theme: DefaultTheme }) => theme.colors.primary};
  }

  @media (max-width: ${({ theme }: { theme: DefaultTheme }) => theme.breakpoints.md}) {
    font-size: 2.25rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.5rem;
  max-width: 800px;
  margin: 0 auto;
  color: ${({ theme }: { theme: DefaultTheme }) => theme.colors.lightGray};
  padding: 0 1rem;

  @media (max-width: ${({ theme }: { theme: DefaultTheme }) => theme.breakpoints.md}) {
    font-size: 1.1rem;
  }
`;

const SplitSection = styled(Section)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;

  @media (max-width: ${({ theme }: { theme: DefaultTheme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ContentSide = styled.div`
  h2 {
    font-size: 2.5rem;
    margin-bottom: 1.5rem;
    position: relative;
    padding-bottom: 0.75rem;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 60px;
      height: 4px;
      background: ${({ theme }: { theme: DefaultTheme }) => theme.colors.primary};
    }
  }

  p {
    font-size: 1.1rem;
    line-height: 1.8;
    color: ${({ theme }: { theme: DefaultTheme }) => theme.colors.lightGray};
    margin-bottom: 1.5rem;
  }
`;

const VisionCard = styled.div`
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme.colors.darker};
  padding: 3rem;
  border-radius: ${({ theme }: { theme: DefaultTheme }) => theme.borderRadius.medium};
  border-left: 5px solid ${({ theme }: { theme: DefaultTheme }) => theme.colors.primary};
  box-shadow: ${({ theme }: { theme: DefaultTheme }) => theme.shadows.medium};
`;

const VisionQuote = styled.div`
  font-size: 1.5rem;
  font-style: italic;
  margin-bottom: 1.5rem;
  color: ${({ theme }: { theme: DefaultTheme }) => theme.colors.light};
  display: flex;
  
  svg {
    color: ${({ theme }: { theme: DefaultTheme }) => theme.colors.primary};
    margin-right: 1rem;
    flex-shrink: 0;
  }
`;

const GridSection = styled(Section)`
  text-align: center;
`;

const SectionHeader = styled.div`
  margin-bottom: 4rem;
  
  h2 {
    font-size: 2.5rem;
    margin-bottom: 1rem;
  }
  
  p {
    max-width: 700px;
    margin: 0 auto;
    color: ${({ theme }: { theme: DefaultTheme }) => theme.colors.lightGray};
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
`;

const FeatureCard = styled.div`
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme.colors.darker};
  padding: 2.5rem;
  border-radius: ${({ theme }: { theme: DefaultTheme }) => theme.borderRadius.medium};
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
  }
`;

const IconBox = styled.div<{ color?: string }>`
  width: 60px;
  height: 60px;
  background: ${({ theme, color }: { theme: DefaultTheme; color?: string }) => color || theme.colors.primary};
  border-radius: ${({ theme }: { theme: DefaultTheme }) => theme.borderRadius.small};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  color: white;
  font-size: 1.5rem;
`;

const StatsSection = styled.section`
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme.colors.dark};
  padding: 4rem 0;
`;

const StatsGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  text-align: center;
  padding: 0 1rem;

  @media (max-width: ${({ theme }: { theme: DefaultTheme }) => theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StatItem = styled.div`
  h3 {
    font-size: 3rem;
    color: ${({ theme }: { theme: DefaultTheme }) => theme.colors.primary};
    margin-bottom: 0.5rem;
  }
  p {
    font-size: 1.1rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: ${({ theme }: { theme: DefaultTheme }) => theme.colors.lightGray};
  }
`;

const AboutUsPage: React.FC = () => {
  return (
    <PageContainer>
      <Helmet>
        <title>About Us | ElectroCable</title>
        <meta name="description" content="Learn about ElectroCable's history, vision, and commitment to excellence in the wire and cable industry." />
      </Helmet>

      <HeroSection>
        <div>
          <HeroTitle>Our <span>Connection</span> to Excellence</HeroTitle>
          <HeroSubtitle>Providing world-class wire and cable solutions with integrity and quality.</HeroSubtitle>
        </div>
      </HeroSection>

      <StatsSection>
        <StatsGrid>
          <StatItem>
            <h3>Years</h3>
            <p>Of Experience</p>
          </StatItem>
          <StatItem>
            <h3>100%</h3>
            <p>Transparency</p>
          </StatItem>
          <StatItem>
            <h3>USA</h3>
            <p>Based Manufacturing</p>
          </StatItem>
          <StatItem>
            <h3>UL</h3>
            <p>Listed Quality</p>
          </StatItem>
        </StatsGrid>
      </StatsSection>

      <SplitSection>
        <ContentSide>
          <h2>Our Vision</h2>
          <p>
            To be a preferred provider of solutions to the electrical industry by providing innovative products and services with exceptional quality and delivering on our promises.

            ElectroCable is a leading manufacturer of industrial, commercial & specialty wire & cable. We manufacture high-quality wire & cable products for the electrical distribution channel & associated markets. Our state-of-the-art manufacturing facilities in South Korea and Ecuador produce a full array of wire & cable in standard & special constructions.
            We offer our products in bulk reels & cut to length.
          </p>
          <p>
            We strive to be the preferred partner for electrical contractors and distributors nationwide, combining superior
            manufacturing quality with unrivaled service and technical expertise.
          </p>
          <VisionCard>
            <VisionQuote>
              <IconWrapper icon={FaQuoteLeft} size={20} />
              Industry Leading Wire & Cable Manufacturer
            </VisionQuote>
            <p style={{ margin: 0, fontSize: '1rem' }}>
              We are a leading manufacturer of industrial, commercial & specialty wire & cable. We supply the electrical distribution channel & associated markets. Our advanced manufacturing capabilities, supported by our factories in South Korea and Ecuador, enable us to produce wire & cable products of all types. We manufacture a full array of wire & cable in standard & special constructions, engineered to the highest quality standards.
            </p>
          </VisionCard>
        </ContentSide>
        <ContentSide>
          <img
            src="/images/cable-installation.png"
            alt="Electricians installing industrial cabling"
            style={{ width: '100%', borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }}
          />
        </ContentSide>
      </SplitSection>

      <GridSection style={{ backgroundColor: '#111' }}>
        <SectionHeader>
          <h2>Our Values</h2>
          <p>
            Our core values guide every decision we make.
          </p>
        </SectionHeader>
        <FeatureGrid>
          <FeatureCard>
            <IconBox><IconWrapper icon={FaHistory} /></IconBox>
            <h3>Rich Heritage</h3>
            <p>We have evolved from a local manufacturer to a global leader in aluminum and copper wire & cable, with advanced manufacturing facilities in the USA, South Korea, and Ecuador.</p>
          </FeatureCard>
          <FeatureCard>
            <IconBox><IconWrapper icon={FaHandsHelping} /></IconBox>
            <h3>100% Transparency</h3>
            <p>We believe in open communication and honest business practices. Our partners can always count on us to provide accurate information and reliable service.</p>
          </FeatureCard>
          <FeatureCard>
            <IconBox><IconWrapper icon={FaLeaf} /></IconBox>
            <h3>Sustainability</h3>
            <p>We are committed to environmental stewardship, exercising responsibility in every aspect of our supply chain.</p>
          </FeatureCard>
        </FeatureGrid>
      </GridSection>

      <GridSection>
        <SectionHeader>
          <h2>Powering a Greener Future</h2>
          <p>Tailored solutions tailored for the next generation of energy infrastructure.</p>
        </SectionHeader>
        <FeatureGrid>
          <FeatureCard>
            <IconBox color="#2ecc71"><IconWrapper icon={FaChargingStation} /></IconBox>
            <h3>EV Charging</h3>
            <p>We provide the high-performance cabling required for the rapidly expanding electric vehicle charging network.</p>
          </FeatureCard>
          <FeatureCard>
            <IconBox color="#f1c40f"><IconWrapper icon={FaSolarPanel} /></IconBox>
            <h3>Renewables</h3>
            <p>Supporting the transition to green energy with specialized wire for solar, wind, and battery storage systems.</p>
          </FeatureCard>
          <FeatureCard>
            <IconBox color="#3498db"><IconWrapper icon={FaBolt} /></IconBox>
            <h3>Utilities</h3>
            <p>Strengthening the nation's power grid with reliable transmission solutions for modern utility infrastructure.</p>
          </FeatureCard>
        </FeatureGrid>
      </GridSection>
    </PageContainer>
  );
};

export default AboutUsPage;
